// src/pages/AttendancePanel.js
import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
axios.defaults.withCredentials = true;

const fmtTime = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  // DB useUTC=true → JSON date 的 UTC 分量 = SQL 本地時間
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const fmtDuration = (min) => {
  if (min == null) return '-';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const liveDuration = (checkIn) => {
  if (!checkIn) return '-';
  const d = new Date(checkIn);
  // DB useUTC=true → UTC 分量是 SQL 本地時間，需轉為正確的 local timestamp
  const corrected = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
    d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds());
  const diff = Math.floor((Date.now() - corrected.getTime()) / 60000);
  if (diff < 0) return '0m';
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

function EditLabelModal({ open, onClose, row, onSaved }) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && row) setLabel(row.LABEL || '');
  }, [open, row]);

  if (!open || !row) return null;

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`/api/admin/attendance/${row.ID}/label`, { label });
      onSaved?.();
      onClose();
    } catch {
      alert('儲存失敗');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 400, background: '#fff', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,.25)', padding: 16 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 900 }}>編輯標籤</h3>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>條碼：{row.BARCODE}</div>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="輸入名稱或標籤"
          style={{ width: '100%', padding: '10px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 8, boxSizing: 'border-box' }}
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && save()}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '8px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>取消</button>
          <button onClick={save} disabled={busy} style={{ padding: '8px 14px', fontSize: 14, borderRadius: 8, border: 'none', background: '#111', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            {busy ? '儲存中…' : '儲存'}
          </button>
        </div>
      </div>
    </div>
  );
}

const AttendancePanel = forwardRef(function AttendancePanel(_, ref) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [tick, setTick] = useState(0);
  const mountedRef = useRef(true);

  const fetchList = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setErr('');
      const { data } = await axios.get('/api/admin/attendance/today');
      if (mountedRef.current) {
        setList(Array.isArray(data?.list) ? data.list : []);
      }
    } catch {
      if (mountedRef.current) setErr('讀取失敗');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useImperativeHandle(ref, () => ({ refresh: () => fetchList(true) }), [fetchList]);

  useEffect(() => {
    mountedRef.current = true;
    fetchList();
    return () => { mountedRef.current = false; };
  }, [fetchList]);

  // 每 60 秒刷新（更新「進場中」的即時時長）
  useEffect(() => {
    const t = setInterval(() => {
      setTick((v) => v + 1);
      fetchList(true);
    }, 60000);
    return () => clearInterval(t);
  }, [fetchList]);

  const inCount = list.filter((r) => !r.CHECK_OUT).length;
  const outCount = list.filter((r) => !!r.CHECK_OUT).length;

  return (
    <section style={st.container}>
      <div style={st.header}>
        <div style={{
          fontWeight: 1000, fontSize: 18,
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
        }}>
          進出場記錄
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...st.badge, background: '#fef3c7', color: '#92400e' }}>進場中 {inCount}</span>
          <span style={{ ...st.badge, background: '#d1fae5', color: '#065f46' }}>已出場 {outCount}</span>
          {loading && <span style={{ fontSize: 12, color: '#555' }}>更新中…</span>}
        </div>
      </div>

      <div style={st.listWrap}>
        {loading && list.length === 0 && <div style={{ padding: 8, fontSize: 14, color: '#666' }}>讀取中…</div>}
        {err && <div style={{ padding: 8, fontSize: 14, color: '#b91c1c' }}>{err}</div>}
        {!loading && !err && list.length === 0 && <div style={{ padding: 8, fontSize: 14, color: '#999' }}>今天沒有進出場記錄</div>}

        <div style={{ display: 'grid', gap: 8 }}>
          {list.map((r) => {
            const isOut = !!r.CHECK_OUT;
            return (
              <div
                key={r.ID}
                style={{
                  borderRadius: 12,
                  border: `2px solid ${isOut ? '#10b981' : '#f59e0b'}`,
                  background: isOut ? '#ecfdf5' : '#fffbeb',
                  padding: '12px 14px',
                  display: 'grid',
                  gap: 6,
                  boxShadow: isOut
                    ? '0 2px 8px rgba(16, 185, 129, 0.12)'
                    : '0 2px 8px rgba(245, 158, 11, 0.12)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 900, fontFamily: 'monospace', color: '#333' }}>
                      {r.BARCODE}
                    </span>
                    {r.LABEL && (
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', padding: '2px 6px', borderRadius: 6 }}>
                        {r.LABEL}
                      </span>
                    )}
                    {r.CUSTOMER_TYPE && (
                      <span style={{
                        fontSize: 11, fontWeight: 900, padding: '2px 6px', borderRadius: 999, color: '#fff',
                        background: r.CUSTOMER_TYPE === '資深年會員' ? '#7c3aed' : r.CUSTOMER_TYPE === '年會員' ? '#2563eb' : r.CUSTOMER_TYPE === '月會員' ? '#f97316' : '#6b7280',
                      }}>
                        {r.CUSTOMER_TYPE}
                      </span>
                    )}
                    {r.IS_NIGHT_OWL ? (
                      <span style={{ fontSize: 11, fontWeight: 900, padding: '2px 6px', borderRadius: 999, background: '#7c3aed', color: '#fff' }}>
                        夜貓
                      </span>
                    ) : null}
                  </div>
                  <button
                    onClick={() => setEditRow(r)}
                    style={{ fontSize: 11, padding: '3px 6px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
                    title="編輯標籤"
                  >
                    標籤
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>
                    進場 <b style={{ fontSize: 15 }}>{fmtTime(r.CHECK_IN)}</b>
                  </span>
                  <span>
                    出場 <b style={{ fontSize: 15 }}>{isOut ? fmtTime(r.CHECK_OUT) : '—'}</b>
                  </span>
                  <span style={{
                    fontWeight: 900, fontSize: 14,
                    color: isOut ? '#059669' : '#d97706',
                  }}>
                    {isOut ? fmtDuration(r.DURATION_MIN) : `${liveDuration(r.CHECK_IN)} (進場中)`}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, flexWrap: 'wrap', color: '#555' }}>
                  {r.PAID_HOURS && <span>付{r.PAID_HOURS}h</span>}
                  {r.AMOUNT_PAID != null && <span>已付 <b style={{ color: '#059669' }}>${r.AMOUNT_PAID}</b></span>}
                  {isOut && r.AMOUNT_DUE > 0 && <span>補繳 <b style={{ color: '#dc2626' }}>${r.AMOUNT_DUE}</b></span>}
                  {isOut && r.TOTAL_AMOUNT != null && <span>總額 <b style={{ color: '#1e40af' }}>${r.TOTAL_AMOUNT}</b></span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <EditLabelModal
        open={!!editRow}
        onClose={() => setEditRow(null)}
        row={editRow}
        onSaved={() => fetchList(true)}
      />
    </section>
  );
});

export default AttendancePanel;

const st = {
  container: {
    minWidth: 0,
    width: '100%',
    height: '100%',
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    gap: 10,
    border: '2px solid #d1fae5',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
    padding: 12,
    boxSizing: 'border-box',
    minHeight: 0,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.08)',
    position: 'relative',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingBottom: 8,
    borderBottom: '2px solid rgba(16, 185, 129, 0.15)',
  },
  badge: {
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
  },
  listWrap: {
    minHeight: 0,
    height: '100%',
    overflowY: 'auto',
    paddingRight: 4,
  },
};
